/**
 * Bill Types
 *
 * Type definitions for bills, deposits, and related entities.
 */

export enum BillType {
  TICKET = 'ticket',
  INVOICE = 'invoice',
  CREDIT_NOTE = 'credit_note',
  DEBIT_NOTE = 'debit_note',
  PURCHASE = 'purchase',
  EXPORT = 'export',
  PROFORMA = 'proforma',
}

export const BILL_TYPE_LABELS: Record<BillType, string> = {
  [BillType.TICKET]: 'Tiquete Electrónico',
  [BillType.INVOICE]: 'Factura Electrónica',
  [BillType.CREDIT_NOTE]: 'Nota de Crédito',
  [BillType.DEBIT_NOTE]: 'Nota de Débito',
  [BillType.PURCHASE]: 'Comprobante de Compra',
  [BillType.EXPORT]: 'Factura de Exportación',
  [BillType.PROFORMA]: 'Proforma',
}

export enum BillStatus {
  PENDING = 'pending',
  FINALIZED = 'finalized',
  CANCELLED = 'cancelled',
  CONTINUE_LATER = 'continue_later',
  PAID = 'paid',
  CREDIT = 'credit',
}

export const BILL_STATUS_LABELS: Record<BillStatus, string> = {
  [BillStatus.PENDING]: 'Pendiente',
  [BillStatus.FINALIZED]: 'Finalizada',
  [BillStatus.CANCELLED]: 'Anulada',
  [BillStatus.CONTINUE_LATER]: 'Continuar después',
  [BillStatus.PAID]: 'Pagada',
  [BillStatus.CREDIT]: 'Crédito',
}

export const BILL_STATUS_COLORS: Record<BillStatus, string> = {
  [BillStatus.PENDING]: 'bg-yellow-100 text-yellow-800',
  [BillStatus.FINALIZED]: 'bg-blue-100 text-blue-800',
  [BillStatus.CANCELLED]: 'bg-red-100 text-red-800',
  [BillStatus.CONTINUE_LATER]: 'bg-gray-100 text-gray-800',
  [BillStatus.PAID]: 'bg-green-100 text-green-800',
  [BillStatus.CREDIT]: 'bg-orange-100 text-orange-800',
}

export enum PaymentType {
  CASH = 'cash',
  CARD = 'card',
  CREDIT = 'credit',
  TRANSFER = 'transfer',
  SINPE = 'sinpe',
  CHECK = 'check',
}

export const PAYMENT_TYPE_LABELS: Record<PaymentType, string> = {
  [PaymentType.CASH]: 'Contado',
  [PaymentType.CARD]: 'Contado',
  [PaymentType.CREDIT]: 'Crédito',
  [PaymentType.TRANSFER]: 'Contado',
  [PaymentType.SINPE]: 'Contado',
  [PaymentType.CHECK]: 'Contado',
}

export const PAYMENT_METHOD_LABELS: Record<PaymentType, string> = {
  [PaymentType.CASH]: 'Efectivo',
  [PaymentType.CARD]: 'Tarjeta',
  [PaymentType.CREDIT]: 'Crédito',
  [PaymentType.TRANSFER]: 'Transferencia',
  [PaymentType.SINPE]: 'SINPE',
  [PaymentType.CHECK]: 'Cheque',
}

export const PAYMENT_METHOD_CODES: Record<string, string> = {
  '01': 'Efectivo',
  '02': 'Tarjeta',
  '03': 'Cheque',
  '04': 'Transferencia',
  '05': 'Recaudado por terceros',
  '06': 'SINPE Móvil',
  '07': 'Plataformas Digitales',
  '99': 'Otros',
}

export enum HaciendaStatus {
  PENDING = 'pending',
  SUBMITTED = 'submitted',
  SUCCESS = 'success',
  ACCEPTED = 'accepted',
  FAILED = 'failed',
  REJECTED = 'rejected',
  RETRYING = 'retrying',
  DISABLED = 'disabled',
  NO_CREDENTIALS = 'no_credentials',
  CONFIG_ERROR = 'config_error',
}

export const HACIENDA_STATUS_LABELS: Record<string, string> = {
  pending: 'Pendiente',
  submitted: 'Enviado',
  in_process: 'En proceso',
  received: 'Recibido',
  success: 'Aceptado',
  accepted: 'Aceptado',
  failed: 'Error',
  rejected: 'Rechazado',
  error: 'Error de envío',
  retrying: 'Reintentando',
  disabled: 'Deshabilitado',
  no_credentials: 'Sin credenciales',
  config_error: 'Error configuración',
}

export const HACIENDA_STATUS_COLORS: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  submitted: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  in_process: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  received: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  success: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  accepted: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  rejected: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  error: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  retrying: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  disabled: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
  no_credentials: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  config_error: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
}

export interface BillLine {
  id: number
  article_id: number
  article_code?: string
  article_description?: string
  quantity: number
  unit_price: number
  discount_percent: number
  subtotal: number
  total: number
  tax_rate?: number
  cabys_code?: string
}

export interface Deposit {
  id: number
  amount: number
  payment_type?: string
  notes?: string
  created_at: string
  user_name?: string
}

export interface Bill {
  id: number
  bill_number?: string
  bill_type: BillType
  status: BillStatus
  client_id?: number
  client_name?: string
  general_discount: number
  subtotal: number
  subtotal_2: number
  total_general_discount: number
  total_line_discount: number
  total_sale_tax: number
  total: number
  total_deposited?: number
  payment_type?: PaymentType
  notes?: string
  created_at?: string
  expiration_date?: string | null
  user_id: number
  activity_code?: string
  original_invoice_id?: number
}

export interface CreditNoteSummary {
  id: number
  bill_number?: string
  total: number
  created_at?: string
  hacienda_status?: string
}

export interface BillDetail extends Bill {
  // Line items and deposits
  lines: BillLine[]
  deposits: Deposit[]

  // Client details
  client_identification?: string
  client_id_type?: string
  client_email?: string
  client_phone?: string

  // Payment details
  payment_method_code?: string
  term_days?: number
  total_client_pay?: number
  total_client_change?: number
  card_transaction_number?: string

  // Hacienda v4.4
  clave_hacienda?: string
  consecutivo_hacienda?: string
  hacienda_status?: string
  hacienda_message?: string
  /**
   * Structured, classified errors parsed out of `hacienda_message` (1..N items).
   * Backend `incident_classifier.classify_errors()` is the source of truth.
   */
  hacienda_errors?: Array<{
    category: string
    code: string | null
    raw: string
    target: string | null
    target_label?: string | null
  }>
  alanube_document_id?: string
  alanube_pdf_url?: string

  // Exoneration
  is_exonerated: boolean
  exoneration_type?: string
  exoneration_number?: string
  exoneration_institution?: string
  exoneration_percentage?: number

  // Economic activity
  receiver_activity_code?: string

  // User/Creator
  user_name?: string

  // Currency
  currency: string
  dolar_price?: number

  // Calculated
  remaining_debt?: number

  // Credit note relationship
  credit_notes?: CreditNoteSummary[]
  original_invoice_number?: string
}

export interface SubmissionAttempt {
  id: number
  bill_id: number
  attempt_number: number
  status: string
  error_message?: string
  error_code?: string
  request_payload?: string
  response_payload?: string
  created_at: string
}

export interface BillFilters {
  status?: BillStatus
  bill_type?: BillType
  client_id?: number
  date_from?: string
  date_to?: string
  hacienda_status?: HaciendaStatus
  page?: number
  page_size?: number
  search?: string
}

export interface DepositCreate {
  amount: number
  payment_type: PaymentType
  notes?: string
}

export interface BillTotals {
  subtotal: number
  subtotal_2: number
  total_general_discount: number
  total_line_discount: number
  total_sale_tax: number
  total: number
  total_deposited: number
  total_debts: number
}

// ============================================================================
// Reference Codes for Credit/Debit Notes (Hacienda v4.4)
// ============================================================================

export enum ReferenceCode {
  ANULA_DOCUMENTO = '01',
  CORRIGE_TEXTO = '02',
  CORRIGE_MONTO = '03',
  REFERENCIA_OTRO = '04',
  SUSTITUYE_PROFORMA = '05',
  DEVOLUCION_MERCANCIA = '06',
  SUSTITUYE_COMPROBANTE = '07',
  FACTURA_ENDOSADA = '08',
  NC_FINANCIERA = '09',
  ND_FINANCIERA = '10',
  PROVEEDOR_NO_DOMICILIADO = '11',
  CREDITO_EXONERACION_POSTERIOR = '12',
}

export const REFERENCE_CODE_LABELS: Record<ReferenceCode, string> = {
  [ReferenceCode.ANULA_DOCUMENTO]: 'Anulación - Cancela la factura completa',
  [ReferenceCode.CORRIGE_TEXTO]: 'Corrige texto del documento',
  [ReferenceCode.CORRIGE_MONTO]: 'Corrige monto del documento',
  [ReferenceCode.REFERENCIA_OTRO]: 'Referencia a otro documento',
  [ReferenceCode.SUSTITUYE_PROFORMA]: 'Sustituye comprobante provisional',
  [ReferenceCode.DEVOLUCION_MERCANCIA]: 'Devolución - Retorno de productos',
  [ReferenceCode.SUSTITUYE_COMPROBANTE]: 'Sustituye comprobante electrónico',
  [ReferenceCode.FACTURA_ENDOSADA]: 'Factura endosada',
  [ReferenceCode.NC_FINANCIERA]: 'Nota de crédito financiera',
  [ReferenceCode.ND_FINANCIERA]: 'Nota de débito financiera',
  [ReferenceCode.PROVEEDOR_NO_DOMICILIADO]: 'Proveedor no domiciliado',
  [ReferenceCode.CREDITO_EXONERACION_POSTERIOR]: 'Crédito exoneración posterior',
}

// Reference codes valid for Credit Notes (simplified for common use cases)
// 01 = Anulación total de la factura
// 06 = Devolución de mercancía (parcial o total)
export const CREDIT_NOTE_REFERENCE_CODES: ReferenceCode[] = [
  ReferenceCode.ANULA_DOCUMENTO,
  ReferenceCode.DEVOLUCION_MERCANCIA,
]

// Reference codes valid for Debit Notes
export const DEBIT_NOTE_REFERENCE_CODES: ReferenceCode[] = [
  ReferenceCode.CORRIGE_MONTO,
  ReferenceCode.ND_FINANCIERA,
]

// ============================================================================
// Credit Note / Debit Note Request Types
// ============================================================================

export interface CreditNoteLineRequest {
  original_line_id: number
  quantity: number
  unit_price: number
}

export interface CreditNoteCreateRequest {
  reference_code: ReferenceCode | string
  reason?: string
  lines: CreditNoteLineRequest[]
}

export interface DebitNoteLineRequest {
  article_id?: number
  description: string
  quantity: number
  unit_price: number
  cabys_code: string
}

export interface DebitNoteCreateRequest {
  reference_code: ReferenceCode | string
  reason?: string
  lines: DebitNoteLineRequest[]
}

export interface NoteResponse {
  success: boolean
  note_id?: number
  credit_note_id?: number
  document_key?: string
  consecutive?: string
  hacienda_status?: string
  message: string
}
